<template>
  <CModal :show="show" :closeOnBackdrop="false" :centered="true" size="lg">
    <template #header>
      <h5 class="modal-title">{{ title }}</h5>
    </template>

    <div class="p-3">
      <div class="mb-4">
        <p>
          {{ $t("models.market_plan.exceptions.description") }}
        </p>
      </div>

      <div class="form-group">
        <label>{{ $t("models.market_plan.exceptions.addDate") }}</label>

        <div class="form-group d-flex flex-row align-items-center">
          <CInput v-model="newExceptionDate" type="date" class="m-0 mr-2" />
          <CButton color="primary" size="sm" class="py-2" @click="addException">
            <font-awesome-icon icon="plus" />
            {{ $t("models.market_plan.exceptions.addDate") }}
          </CButton>
        </div>
      </div>

      <div v-if="exceptions.length > 0" class="mt-4">
        <label>{{ $t("models.market_plan.exceptions.existingDates") }}</label>
        <div class="w-100">
          <div
            v-for="(exception, index) in exceptions"
            :key="index"
            class="badge badge-gray-300 mr-2 pl-2"
          >
            <span>{{ formatDate(exception) }}</span>
            <CButton
              color="gray-300"
              size="sm"
              @click="removeException(index)"
              class="ml-2"
            >
              <font-awesome-icon icon="times" />
            </CButton>
          </div>
        </div>
      </div>

      <div v-else class="alert alert-info mt-4">
        {{ $t("models.market_plan.exceptions.noExceptions") }}
      </div>
    </div>

    <CAlert v-if="validationError" color="danger" :show.sync="showAlert">
      {{ validationError }}
    </CAlert>

    <template #footer>
      <CButton color="primary" @click="closeModal" variant="outline">
        {{ $t("common.editor.button.cancel") }}
      </CButton>
      <CButton
        color="primary"
        @click="saveExceptions"
        :disabled="exceptions.length < 1"
      >
        {{ $t("common.editor.button.save") }}
      </CButton>
    </template>
  </CModal>
</template>

<script>
import { apiPatch } from "../../../helpers/apiHelper";

export default {
  name: "ExceptionsModal",

  props: {
    show: {
      type: Boolean,
      default: false,
    },
    title: {
      type: String,
      default: "Manage Exceptions",
    },
    planningId: {
      type: [Number, String],
      default: null,
    },
    currentRule: {
      type: String,
      default: "",
    },
  },

  data() {
    return {
      exceptions: [],
      newExceptionDate: null,
      showAlert: false,
      validationError: null,
    };
  },

  watch: {
    show: {
      handler(isShown) {
        if (isShown) {
          this.parseExceptions();
        }
      },
      immediate: false,
    },
    currentRule: {
      handler() {
        if (this.show) {
          this.parseExceptions();
        }
      },
      immediate: false,
    },
  },

  methods: {
    parseExceptions() {
      this.exceptions = [];
      if (!this.currentRule) return;

      const lines = this.currentRule.split("\n");
      lines.forEach((line) => {
        if (line.startsWith("EXDATE:")) {
          const dateStr = line.replace("EXDATE:", "");
          const date = new Date(
            dateStr.replace(
              /(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})Z/,
              "$1-$2-$3T$4:$5:$6Z"
            )
          );
          this.exceptions.push(date.toISOString().split("T")[0]);
        }
      });
      this.exceptions.sort();
    },

    addException() {
      if (
        this.newExceptionDate &&
        !this.exceptions.includes(this.newExceptionDate)
      ) {
        this.exceptions.push(this.newExceptionDate);
        this.exceptions.sort();
        this.newExceptionDate = null;
      }
    },

    removeException(index) {
      this.exceptions.splice(index, 1);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    },

    async saveExceptions() {
      // check user permissions before submitting
      const perm = this.$store.state.connections.current.permissions.role_lg;
      if (perm !== 2) {
        this.validationError = this.$t(
          "models.market_plan.addForm.validation.permissions"
        );
        this.showAlert = true;
        return;
      }

      // Rebuild the rule string with exceptions
      const lines = this.currentRule.split("\n");
      const ruleLines = lines.filter(
        (line) => !line.startsWith("EXDATE:") && line.trim() !== ""
      );

      // Add EXDATE lines
      this.exceptions.forEach((dateString) => {
        const date = new Date(dateString);
        const formatted = date
          .toISOString()
          .replace(/[-:]/g, "")
          .replace(/\.\d{3}/, "");
        ruleLines.push(`EXDATE:${formatted}`);
      });

      const newRuleString = ruleLines.join("\n");

      const response = await apiPatch(
        "role_market_planningdata",
        this.planningId,
        { order_close: newRuleString },
        [this.$store.state.role.id]
      );

      if (response && response.status === 200) {
        this.$store.dispatch("toaster/add", {
          title: "OK!",
          text: this.$t("models.market_plan.exceptions.savedMsg"),
          color: "success",
          autohide: true,
        });
        this.$emit("saved");
        this.closeModal();
      } else {
        this.$store.dispatch("toaster/add", {
          title: this.$t("role.finance.invoicesSettings.attention"),
          text: "Error saving exceptions",
          color: "danger",
          autohide: true,
        });
      }
    },

    closeModal() {
      this.$emit("update:show", false);
      this.showAlert = false;
      this.newExceptionDate = null;
    },
  },
};
</script>
